// A new recipe (WP5.2): choose the product it makes (something sold or a sub-recipe without
// a recipe), then write draft version 1. The baker signs it off on the recipe page.

import type { CatalogueProductSummary } from '@enp/domain/api';
import { newId } from '@enp/domain/ids';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  EmptyState,
  focusRing,
  Input,
  PageHeader,
  Skeleton,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CopyPlus, Lock, Pencil, ScrollText, Search } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { ROLE_LABEL, ROLE_ORDER } from '../../../catalogue/labels.js';
import { productQuery } from '../../../catalogue/queries.js';
import { ApiError } from '../../../lib/api.js';
import { Panel, ProblemNote, useCan } from '../../../production/parts.js';
import {
  createRecipe,
  createVersion,
  invalidateProduction,
  madeProductsQuery,
} from '../../../production/queries.js';
import { recipeProblem, type RecipeProblem } from '../../../production/recipe-dialogs.js';
import { draftFromVersion, RecipeEditor } from '../../../production/recipe-editor.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { actionProblem } from '../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/recipes/new')({
  validateSearch: z.object({ product: z.uuid().optional().catch(undefined) }),
  component: NewRecipePage,
});

const FORM_ID = 'recipe-new';

/** Something made here: sold, or a sub-recipe (not bought in, not a service). */
const canHaveRecipe = (p: CatalogueProductSummary) =>
  p.archived_at === null &&
  (p.roles.includes('intermediate') || p.roles.includes('sellable')) &&
  !p.roles.includes('service');

function Crumbs() {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/recipes">Recipes</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>New recipe</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ChooseProduct({ onChoose }: { onChoose: (id: string) => void }) {
  const [text, setText] = useState('');
  const q = text.trim().length >= 2 ? text.trim() : undefined;
  const list = useQuery(madeProductsQuery(q));
  const found = (list.data?.items ?? []).filter(canHaveRecipe);
  const inputId = useId();
  return (
    <Panel
      id="choose"
      title="Which product is it the recipe of?"
      description="Something you sell or a sub-recipe (like a sponge base). A product has one recipe; later changes are new versions of it."
      className="max-w-2xl"
    >
      <label htmlFor={inputId} className="text-sm font-medium text-fg">
        Search the catalogue
      </label>
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
        />
        <Input
          id={inputId}
          type="search"
          autoFocus
          placeholder="Product name or SKU"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="pl-8"
        />
      </div>
      <div aria-live="polite" className="min-h-40">
        {list.isError ? (
          <ErrorPanel
            error={list.error}
            titleAs="h2"
            what="The products"
            onRetry={() => void list.refetch()}
          />
        ) : list.isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : found.length === 0 ? (
          <p className="text-sm text-muted">
            {q ? `Nothing made here matches “${q}”.` : 'No products that are made here yet.'}
          </p>
        ) : (
          <ul aria-label="Products" className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {found.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-body hover:bg-surface-hover',
                    focusRing,
                  )}
                  onClick={() => onChoose(p.id)}
                >
                  <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-fg">{p.name}</span>
                    {ROLE_ORDER.filter((r) => p.roles.includes(r)).map((r) => (
                      <Badge key={r} tone={r === 'sellable' ? 'primary' : 'neutral'}>
                        {ROLE_LABEL[r]}
                      </Badge>
                    ))}
                  </span>
                  <span className="shrink-0 text-sm text-muted">
                    {p.recipe_state === 'none' ? 'No recipe yet' : 'Has a recipe: open it'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/**
 * A product that already has a recipe (UI5A-13): a product has one recipe, so this goes to it
 * or makes a new draft of it, instead of an editor that fails on save.
 */
function HasRecipe({
  name,
  recipeId,
  draft: waiting,
}: {
  name: string;
  recipeId: string;
  /** A draft already waiting for sign-off: it's opened, never doubled (UI5A-107). */
  draft: { id: string; version_no: number } | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const attempt = useRef(newId());
  const draft = useMutation({
    mutationFn: () => createVersion(recipeId, undefined, attempt.current),
    onSuccess: async (detail) => {
      attempt.current = newId();
      await invalidateProduction(queryClient);
      const made = detail.versions[0];
      toast.success(`Draft version ${made?.version_no ?? ''} made`, {
        description: 'A copy of the latest version. Change it, then ask the baker to sign it off.',
      });
      if (made) {
        void navigate({
          to: '/recipes/$recipeId/versions/$versionId',
          params: { recipeId, versionId: made.id },
        });
      }
    },
  });
  const failed = draft.isError ? actionProblem(draft.error, 'No draft was made') : null;
  return (
    <Panel
      id="has-recipe"
      title={`${name} already has a recipe`}
      description={
        waiting
          ? `A product has one recipe, and draft version ${waiting.version_no} of it is already waiting for the baker’s sign-off. Change that draft rather than starting another.`
          : 'A product has one recipe. To change it, make a new draft version: the version in use stays until the baker signs the draft off.'
      }
      className="max-w-2xl"
    >
      <div className="flex flex-wrap gap-2">
        {waiting ? (
          <Button asChild>
            <Link
              to="/recipes/$recipeId/versions/$versionId"
              params={{ recipeId, versionId: waiting.id }}
            >
              <Pencil aria-hidden />
              Open the draft (version {waiting.version_no})
            </Link>
          </Button>
        ) : (
          <Button
            icon={<CopyPlus aria-hidden />}
            loading={draft.isPending}
            onClick={() => draft.mutate()}
          >
            Make a new draft
          </Button>
        )}
        <Button variant="secondary" asChild>
          <Link to="/recipes/$recipeId" params={{ recipeId }}>
            <ScrollText aria-hidden />
            Open its recipe
          </Link>
        </Button>
      </div>
      {failed && <ProblemNote problem={failed} />}
    </Panel>
  );
}

function NewRecipePage() {
  useDocumentTitle('New recipe');
  const { product: productId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const canEdit = useCan('recipe.edit', true);
  const product = useQuery({ ...productQuery(productId ?? ''), enabled: Boolean(productId) });
  const p = product.data;
  const existing = p?.recipes.find((r) => r.archived_at === null);
  const initial = useMemo(() => draftFromVersion(null, p?.base_uom ?? 'ea'), [p?.base_uom]);

  // One key for this form (D-063, D-065): a double click or a retry makes one recipe.
  const attempt = useRef(newId());
  const create = useMutation({
    mutationFn: (draft: Parameters<typeof createRecipe>[1]) =>
      createRecipe(productId as string, draft, attempt.current),
    onSuccess: async (detail) => {
      queryClient.setQueryData(['production', 'recipe', detail.id], detail);
      await invalidateProduction(queryClient);
      toast.success(`Recipe for ${detail.product.name} saved as a draft`, {
        description: 'Ask the baker to sign it off before it’s used.',
      });
      void navigate({ to: '/recipes/$recipeId', params: { recipeId: detail.id } });
    },
  });
  const exists = create.error instanceof ApiError && create.error.code === 'recipe_exists';
  const failed: RecipeProblem | null = create.isError
    ? exists
      ? {
          title: `${p?.name ?? 'This product'} already has a recipe`,
          explain: 'Open it from the recipe list and make a new draft version there.',
          technical: null,
          lines: [],
          invalid: false,
        }
      : recipeProblem(
          create.error,
          'The recipe wasn’t saved',
          'Change it above (highlighted), then save again.',
        )
    : null;

  const back = (
    <Button variant="secondary" asChild>
      <Link to="/recipes">
        <ArrowLeft aria-hidden />
        Back to recipes
      </Link>
    </Button>
  );

  if (!canEdit) {
    return (
      <>
        <PageHeader eyebrow={<Crumbs />} title="New recipe" />
        <EmptyState
          compact
          icon={Lock}
          title="You can’t write recipes"
          description="Writing recipes needs the “edit recipes” permission and access to every branch. Ask the owner."
          action={back}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={<Crumbs />}
        title={p && !existing ? `New recipe: ${p.name}` : 'New recipe'}
        description="Draft version 1. The baker checks and signs it off before production uses it; until then its cost is worked out from this draft."
        actions={
          p &&
          !existing && (
            <>
              <Button
                variant="secondary"
                onClick={() => void navigate({ search: {} })}
                icon={<ArrowLeft aria-hidden />}
              >
                Another product
              </Button>
              <Button type="submit" form={FORM_ID} loading={create.isPending}>
                Save draft
              </Button>
            </>
          )
        }
      />
      {!productId ? (
        <ChooseProduct onChoose={(id) => void navigate({ search: { product: id } })} />
      ) : product.isError ? (
        <ErrorPanel
          error={product.error}
          titleAs="h2"
          what="The product"
          onRetry={() => void product.refetch()}
          extraAction={back}
        />
      ) : p && existing ? (
        <HasRecipe
          name={p.name}
          recipeId={existing.id}
          draft={existing.versions
            .filter((v) => v.status === 'draft')
            .sort((a, b) => b.version_no - a.version_no)
            .at(0)}
        />
      ) : !p ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <span role="status" className="sr-only">
            Loading the product…
          </span>
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : (
        <RecipeEditor
          key={p.id}
          product={p}
          initial={initial}
          formId={FORM_ID}
          saving={create.isPending}
          problem={failed}
          problemExtra={
            exists && (
              <Link
                to="/recipes"
                search={{ q: p.name }}
                className="text-sm font-medium text-primary-text underline-offset-4 hover:underline"
              >
                Find the recipe for {p.name}
              </Link>
            )
          }
          onSubmit={(draft) => create.mutate(draft)}
          footer={
            <Button variant="secondary" asChild>
              <Link to="/recipes">Cancel</Link>
            </Button>
          }
        />
      )}
    </>
  );
}
